import React, { useState } from 'react';

import { Box, Flex, Grid, Radio } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { components } from 'react-select';

import { useUpdateDeviceLeadsMutation } from '@/store/slices/api/patientsApiSlice';
import { useAddDeviceMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import formatDate from '@/utils/formatDate';
import { isArchivedLead, isInactiveLead, prepareLeadsForSave } from '@/utils/leadChambers';

import { VENDOR_MAPPING } from '@/pages/physician/PatientOverviewPage/types';
import { LabelAndValue } from '@/types/custom';
import { DeviceVendor, Lead, MatchDevice } from '@/types/matchDevice';
import { DeviceLeadLocation } from '@/types/vendorTransmissionReport';
import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import CustomModal from '@/ui/Modal';
import TextInputWrapper from '@/ui/TextInputWrapper';
import TextWrapper from '@/ui/TextWrapper';

import LeadsEditModal from '../../DeviceLeads/LeadsEditModal/newLeads';
import styles from '../DeviceEditModal/DeviceEditModal.module.scss';

export const DEVICE_TYPE_OPTIONS = ['CRT-D', 'CRT-P', 'ILR', 'PM', 'ICD'].map((value) => ({
  label: value,
  value
}));

const ABBOTT_EXTRA_TYPE_OPTIONS = [{ label: 'Leadless PM', value: 'Leadless PM' }];

const getDeviceTypeOptions = (vendor: DeviceVendor | undefined) =>
  vendor === 'abbott'
    ? [...DEVICE_TYPE_OPTIONS, ...ABBOTT_EXTRA_TYPE_OPTIONS]
    : DEVICE_TYPE_OPTIONS;

export const MANUFACTURER_OPTIONS: LabelAndValue[] = [
  { label: 'Abbott', value: 'abbott' },
  { label: 'Boston Scientific', value: 'boston_scientific' },
  { label: 'Medtronic', value: 'medtronic' },
  { label: 'Biotronik', value: 'biotronik' },
  { label: 'Sorin', value: 'sorin' }
];

const MRI_COMPATIBILITY_OPTIONS: LabelAndValue[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const DEVICE_TELEMETRY_OPTIONS: LabelAndValue[] = [
  { label: 'Wireless', value: 'Wireless' },
  { label: 'Manual', value: 'Manual' }
];

interface DeviceAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId?: string;
  devices: MatchDevice[];
  lockDeviceTelemetry?: boolean;
}

type DeviceInfo = {
  modelName?: string;
  modelNumber?: string;
  modelNumber2?: string;
  serialNumber?: string;
  serialNumber2?: string;
  type?: string;
  implantDate?: string | null;
  vendor?: DeviceVendor;
  status?: string;
  patientId?: string;
  id?: string;
  deviceTelemetry?: string;
  isMriCompatibility?: boolean | string | null;
};

const getErrorMessage = (error: any): string => {
  const msg = error?.data?.message;
  if (msg) return Array.isArray(msg) ? msg.join(' ') : String(msg).trim();
  if (error?.message) return String(error.message).trim();
  return 'Failed to update device information.';
};

const REQUIRED_FIELDS: (keyof DeviceInfo)[] = ['vendor', 'type', 'serialNumber', 'implantDate'];

const getRequiredFields = (
  vendor: DeviceVendor | undefined,
  type?: string
): (keyof DeviceInfo)[] => {
  const baseFields: (keyof DeviceInfo)[] = ['vendor', 'type', 'implantDate'];
  if (type === 'Leadless PM') {
    return [...baseFields, 'modelNumber', 'modelNumber2', 'serialNumber', 'serialNumber2'];
  }
  if (vendor === 'biotronik') {
    return [...baseFields, 'modelName', 'serialNumber'];
  }
  return [...baseFields, 'modelNumber', 'serialNumber'];
};

const DeviceAddModal: React.FC<DeviceAddModalProps> = ({
  isOpen,
  onClose,
  patientId,
  devices,
  lockDeviceTelemetry = false
}) => {
  const [formDeviceInfo, setFormDeviceInfo] = useState<Partial<DeviceInfo>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [addDeviceInfo] = useAddDeviceMutation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditLead, setIsEditLead] = useState(false);
  const [leadsData, setLeadsData] = useState<Lead[] | undefined>([]);
  const [leadStatusErrors, setLeadStatusErrors] = useState<Record<string, string>>({});
  const [showModelSelectModal, setShowModelSelectModal] = useState(false);
  const [modelSelectMode, setModelSelectMode] = useState<'import' | 'manual'>('import');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [isDeviceExists, setIsDeviceExists] = useState<boolean>(false);
  const [isMultiDeviceFound, setIsMultiDeviceFound] = useState<boolean>(false);
  const [updateDeviceLeads] = useUpdateDeviceLeadsMutation();

  // Custom Option component for device dropdown
  const CustomDeviceOption = (props: any) => {
    const device = devices.find((d) => d.id === props.data.value);

    return (
      <components.Option {...props}>
        <div>
          {props.data.label}
          {device?.implantDate && (
            <div>Implanted: {formatDate(device.implantDate, 'MMM YYYY')}</div>
          )}
        </div>
      </components.Option>
    );
  };

  const validate = () => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor, formDeviceInfo.type);
    const newErrors: Record<string, string> = {};
    requiredFields.forEach((field) => {
      if (!formDeviceInfo[field] || String(formDeviceInfo[field]).trim() === '') {
        newErrors[field] = 'Required';
      }
    });

    const enteredSN = formDeviceInfo.serialNumber?.trim().toLowerCase();
    if (enteredSN) {
      const duplicate = devices.some((d) => d.serialNumber?.trim().toLowerCase() === enteredSN);
      if (duplicate) newErrors['serialNumber'] = 'A device with this serial number already exists.';
    }
    if (formDeviceInfo.type === 'Leadless PM') {
      const enteredSN2 = formDeviceInfo.serialNumber2?.trim().toLowerCase();
      if (enteredSN2) {
        const duplicate2 = devices.some(
          (d) => d.serialNumber?.trim().toLowerCase().split(':')[1] === enteredSN2
        );
        if (duplicate2)
          newErrors['serialNumber2'] = 'A device with this serial number already exists.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor, formDeviceInfo.type);
    return requiredFields.every(
      (field) => formDeviceInfo[field] && String(formDeviceInfo[field]).trim() !== ''
    );
  };

  const handleFieldChange = (field: keyof DeviceInfo, value: string | DeviceVendor) => {
    setFormDeviceInfo((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'vendor') {
        const typeOptions = getDeviceTypeOptions(value as DeviceVendor);
        if (prev.type && !typeOptions.find((opt) => opt.value === prev.type)) {
          updated.type = undefined;
        }
      }
      if (field === 'type' && value !== 'Leadless PM') {
        updated.modelNumber2 = undefined;
        updated.serialNumber2 = undefined;
      }
      return updated;
    });

    if (field === 'type') {
      const matchingDevices = devices.filter((d) => d.type === value);
      setIsDeviceExists(matchingDevices.length > 0);
      if (matchingDevices.length === 1) {
        setSelectedModel(matchingDevices[0].id);
        setLeadsData(getLeadsForSelectedDevice(matchingDevices[0].id));
      }
      setIsMultiDeviceFound(matchingDevices.length > 1);
    }

    if (REQUIRED_FIELDS.includes(field)) {
      setErrors((prev) => ({ ...prev, [field]: value ? '' : 'Required' }));
    }
  };

  const handleImplantDateChange = (date: Date | null) => {
    if (date === undefined) return;
    const dateString = date === null ? '' : dayjs(date).format('MM/DD/YYYY');
    handleFieldChange('implantDate', dateString);
  };

  // Types that skip the leads step
  const isILRDevice = formDeviceInfo.type === 'ILR' || formDeviceInfo.type === 'Leadless PM';

  // Button text logic (reacts to type selection)
  const getButtonText = () => {
    if (isEditLead) return 'Save Changes';
    if (isILRDevice) return 'Add Device';
    return 'Next';
  };

  // Next/save handler
  const handlePrimaryAction = async () => {
    if (!validate()) return;
    if (!isEditLead && !isILRDevice) {
      // Check for existing device type
      if (!showModelSelectModal) {
        const matchingDevices = devices.filter((d) => d.type === formDeviceInfo.type);
        if (matchingDevices.length > 0) {
          setShowModelSelectModal(true);
          return;
        } else {
          // No device found, go to leads edit with empty form
          setLeadsData(getLeadsForSelectedDevice('EmptyLeads'));
        }
      }
      setShowModelSelectModal(false);
      setIsEditLead(true);
      return;
    }
    let deviceLeadPayload;
    if (!isILRDevice) {
      const prepared = prepareLeadsForSave(leadsData ?? []);
      if (prepared.error === 'chamber') {
        notifications.show({
          title: 'Missing Lead Chamber',
          message: 'Please select a Chamber for all leads with data.',
          color: 'red'
        });
        return;
      }

      if (prepared.error === 'status') {
        notifications.show({
          title: 'Missing Lead Status',
          message: 'Please select a Status for all leads with data.',
          color: 'red'
        });
        return;
      }

      deviceLeadPayload = prepared.leads.map((lead) => ({
        ...lead,
        id: lead.id?.includes('tempid-') ? undefined : lead.id,
        leadChamber: lead.leadChamber?.trim() || undefined,
        leadManufacturer: lead.leadManufacturer
          ? {
              ...lead.leadManufacturer,
              leadVendorId:
                typeof lead.leadManufacturer === 'object' && 'leadVendorId' in lead.leadManufacturer
                  ? (lead.leadManufacturer as any).leadVendorId
                  : lead.leadManufacturer
            }
          : { leadVendorId: null },
        implantDate: lead.implantDate ? lead.implantDate : null,
        leadLocation: lead.leadLocation ? lead.leadLocation : null
      }));
    }

    // Save device and leads
    setIsSaving(true);
    const payload: DeviceInfo = {
      ...formDeviceInfo,
      status: 'Active',
      implantDate: formDeviceInfo.implantDate
        ? dayjs(formDeviceInfo.implantDate).format('YYYY-MM-DDT00:00:00')
        : null,
      deviceTelemetry: lockDeviceTelemetry
        ? undefined
        : formDeviceInfo.deviceTelemetry?.toString().trim() === '-'
          ? undefined
          : formDeviceInfo.deviceTelemetry,
      isMriCompatibility:
        formDeviceInfo.isMriCompatibility === 'true'
          ? true
          : formDeviceInfo.isMriCompatibility === 'false'
            ? false
            : null,
      type: isLeadlessPM ? 'PM' : formDeviceInfo.type,
      modelNumber: isLeadlessPM
        ? `${formDeviceInfo.modelNumber}:${formDeviceInfo.modelNumber2}`
        : formDeviceInfo.modelNumber,
      serialNumber: isLeadlessPM
        ? `${formDeviceInfo.serialNumber}:${formDeviceInfo.serialNumber2}`
        : formDeviceInfo.serialNumber,
      modelNumber2: undefined,
      serialNumber2: undefined
    };
    try {
      const data = await addDeviceInfo({
        patientId: patientId ?? '',
        ...payload
      } as any).unwrap();
      notifications.show({
        title: 'Success',
        message: 'Device created successfully.',
        color: 'green'
      });
      // Set deviceId for leads
      setLeadsData((prev) => prev?.map((lead) => ({ ...lead, deviceId: data?.id ?? '' })));
      // Only update leads if it's not an ILR device
      if (!isILRDevice) {
        await updateDeviceLeads({
          patientId: patientId ?? '',
          deviceLeads:
            deviceLeadPayload?.map((lead) => ({
              ...lead,
              deviceId: data?.id ?? ''
            })) ?? [],
          deviceId: data?.id ?? '',
          reportId: undefined
        }).unwrap();
      }
      setFormDeviceInfo({});
      setErrors({});
      setIsEditLead(false);
      setLeadsData([]);
      setLeadStatusErrors({});
      setShowModelSelectModal(false);
      setSelectedModel('');
      setModelSelectMode('import');
      setIsMultiDeviceFound(false);
      setIsDeviceExists(false);
      onClose();
    } catch (error) {
      console.error('Device creation error:', error);
      const errorMessage = getErrorMessage(error);
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        autoClose: false
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Lead modal handlers
  const handleLeadFieldChange = (leadId: string, field: keyof Lead, value: any) => {
    setLeadsData((prev) =>
      prev?.map((lead) => (lead.id === leadId ? { ...lead, [field]: value } : lead))
    );
  };
  const handleAddLead = (newLead: Lead) => {
    setLeadsData((prev) => [...(prev || []), newLead]);
  };
  const handleDeleteLead = (leadId: string) => {
    setLeadsData((prev) => prev?.filter((lead) => lead.id !== leadId));
  };
  const handleLeadStatusError = (leadId: string, error?: string) => {
    setLeadStatusErrors((prev) => {
      if (error) return { ...prev, [leadId]: error };
      const { [leadId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleHeadingTextChange = () => {
    if (isMultiDeviceFound && !isEditLead && showModelSelectModal)
      return 'Multiple Device Models Found';
    if (showModelSelectModal && !isEditLead && !isMultiDeviceFound)
      return 'Import Existing Device Data?';
    if (isEditLead) return 'Leads';
    return 'Add Device Info';
  };

  const handleCancel = () => {
    setFormDeviceInfo({});
    setErrors({});
    setIsEditLead(false);
    setLeadsData([]);
    setLeadStatusErrors({});
    setShowModelSelectModal(false);
    setSelectedModel('');
    setModelSelectMode('import');
    setIsMultiDeviceFound(false);
    setIsDeviceExists(false);
    onClose();
  };

  const getLeadsForSelectedDevice = (deviceId: string) => {
    if (!deviceId) return [];
    const chambers: DeviceLeadLocation[] = [
      DeviceLeadLocation.RA,
      DeviceLeadLocation.RV,
      DeviceLeadLocation.LV
    ];
    const importedLeads: Lead[] =
      devices
        .find((d) => d.id === deviceId)
        ?.leads?.filter((lead) => !isInactiveLead(lead.leadStatus) && !isArchivedLead(lead.leadStatus))
        ?.map((lead) => ({
          ...lead,
          id: `tempid-${Math.random().toString(36).slice(2)}`,
          leadStatus: lead.leadStatus || 'Active',
          deviceId: ''
        })) ?? [];
    const importedChambers = new Set(
      importedLeads.map((lead) => lead.leadChamber?.trim().toLowerCase()).filter(Boolean)
    );
    const missingStandardLeads: Lead[] = chambers
      .filter((chamber) => !importedChambers.has(chamber.toLowerCase()))
      .map((chamber) => ({
        id: `tempid-${Math.random().toString(36).slice(2)}`,
        leadChamber: chamber,
        leadLocation: undefined,
        implantDate: undefined,
        serialNumber: undefined,
        leadCapped: undefined,
        leadStatus: undefined,
        deviceId: '',
        leadModel: undefined,
        leadManufacturer: { leadVendorId: null }
      }));

    // Preserve custom chambers and multiple leads in the same chamber during import.
    return [...importedLeads, ...missingStandardLeads];
  };

  const renderSelect = (
    label: string,
    options: LabelAndValue[],
    field: keyof DeviceInfo,
    value: string | DeviceVendor | undefined,
    isDisabled = false
  ) => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor, formDeviceInfo.type);
    const selected = options.find((opt) => String(opt.value) === String(value));
    return (
      <Dropdown
        label={label}
        options={options}
        value={selected}
        onChange={(option) => handleFieldChange(field, (option as LabelAndValue).value)}
        placeholder={`Select ${label}`}
        labelTextProps={{ variant: 'regular' }}
        required={!isDisabled && requiredFields.includes(field)}
        isDisabled={isDisabled}
      />
    );
  };

  const renderTextInput = (label: string, field: keyof DeviceInfo) => {
    const requiredFields = getRequiredFields(formDeviceInfo.vendor, formDeviceInfo.type);
    return (
      <TextInputWrapper
        label={label}
        value={
          typeof formDeviceInfo[field] === 'boolean'
            ? String(formDeviceInfo[field])
            : (formDeviceInfo[field] ?? '')
        }
        onChange={(e) => handleFieldChange(field, e.target.value)}
        error={errors[field]}
        required={requiredFields.includes(field)}
      />
    );
  };

  const isLeadlessPM = formDeviceInfo.type === 'Leadless PM';

  // Helper for device model dropdown with styled options
  const getDeviceModelOptionsAndValue = () => {
    const options = devices
      .filter((d) => d.type === formDeviceInfo.type)
      .map((d) => ({
        label: `${VENDOR_MAPPING[d.vendor]} - ${d.type}`,
        value: d.id
      }));
    const value = options.find((opt) => opt.value === selectedModel);
    return { options, value };
  };

  return (
    <>
      <CustomModal
        modalProps={{
          opened: isOpen,
          onClose: handleCancel,
          size: 'xl',
          closeOnClickOutside: false,
          closeOnEscape: false
        }}
        header={{
          title: handleHeadingTextChange()
        }}
        primaryButton={{
          text: getButtonText(),
          isLoading: isSaving,
          onClick: handlePrimaryAction,
          isDisabled: isSaving || !isFormValid()
        }}
        secondaryButton={{ text: 'Cancel', isDisabled: isSaving, onClick: handleCancel }}>
        {showModelSelectModal ? (
          <Box p={20}>
            <Flex align="center">
              <Box>
                <TextWrapper
                  text={
                    isMultiDeviceFound
                      ? 'Duplicate device detected. Import Leads or enter manually?'
                      : 'Choose a device model to import leads, or enter manually.'
                  }
                  variant="heading"
                />
              </Box>
            </Flex>
            <Box>
              <Radio.Group
                value={modelSelectMode}
                onChange={(val) => {
                  setModelSelectMode(val as 'import' | 'manual');
                  if (val === 'import') {
                    if (getDeviceModelOptionsAndValue().options.length === 1) {
                      setSelectedModel(getDeviceModelOptionsAndValue().options[0].value);
                      setLeadsData(
                        getLeadsForSelectedDevice(getDeviceModelOptionsAndValue().options[0].value)
                      );
                    }
                    setIsDeviceExists(true);
                  } else {
                    setLeadsData(getLeadsForSelectedDevice('EmptyLeads'));
                    setIsDeviceExists(false);
                  }
                }}>
                <Flex gap={25} mt={8}>
                  <Radio value="import" label="Import from Device" />
                  <Radio value="manual" label="Enter Manually" />
                </Flex>
              </Radio.Group>
            </Box>
            {modelSelectMode === 'import' && isDeviceExists && (
              <Box w={'50%'}>
                <TextWrapper text="Device Model" size="sm" variant="normal" mt={20} />
                <Dropdown
                  options={getDeviceModelOptionsAndValue().options}
                  value={getDeviceModelOptionsAndValue().value}
                  onChange={(option) => {
                    setSelectedModel(option?.value ?? '');
                    setLeadsData(getLeadsForSelectedDevice(option?.value ?? ''));
                  }}
                  placeholder="Select a Model"
                  customComponents={{
                    Option: CustomDeviceOption
                  }}
                />
              </Box>
            )}
          </Box>
        ) : isEditLead ? (
          <LeadsEditModal
            leadsData={leadsData}
            onLeadFieldChange={handleLeadFieldChange}
            onAddLead={handleAddLead}
            onDeleteLead={handleDeleteLead}
            leadStatusErrors={leadStatusErrors}
            onLeadStatusError={handleLeadStatusError}
            deviceId={formDeviceInfo.id as string}
          />
        ) : (
          <div className={styles.deviceSection}>
            <Grid mt="md" align="center">
              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect(
                  'Manufacturer',
                  MANUFACTURER_OPTIONS,
                  'vendor',
                  formDeviceInfo.vendor
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect(
                  'Type',
                  getDeviceTypeOptions(formDeviceInfo.vendor),
                  'type',
                  formDeviceInfo.type
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderTextInput('Model Name', 'modelName')}
              </Grid.Col>

              {isLeadlessPM ? (
                <>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    {renderTextInput('Model Number 1', 'modelNumber')}
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    {renderTextInput('Model Number 2', 'modelNumber2')}
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    {renderTextInput('Serial Number 1', 'serialNumber')}
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    {renderTextInput('Serial Number 2', 'serialNumber2')}
                  </Grid.Col>
                </>
              ) : (
                <>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    {renderTextInput('Model Number', 'modelNumber')}
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    {renderTextInput('Serial Number', 'serialNumber')}
                  </Grid.Col>
                </>
              )}

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <DatePickerWrapper
                  inputProps={{ label: 'Implant Date' }}
                  datePickerProps={{
                    id: 'implantDate',
                    selected:
                      formDeviceInfo.implantDate && formDeviceInfo.implantDate !== '-'
                        ? dayjs(formDeviceInfo.implantDate).toDate()
                        : null,
                    onChange: (date: Date | null) => handleImplantDateChange(date),
                    isClearable: true,
                    required: true
                  }}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect(
                  'Device Telemetry',
                  DEVICE_TELEMETRY_OPTIONS,
                  'deviceTelemetry',
                  String(formDeviceInfo.deviceTelemetry),
                  lockDeviceTelemetry
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                {renderSelect(
                  'MRI Compatibility',
                  MRI_COMPATIBILITY_OPTIONS,
                  'isMriCompatibility',
                  String(formDeviceInfo.isMriCompatibility)
                )}
              </Grid.Col>
            </Grid>
          </div>
        )}
      </CustomModal>
    </>
  );
};

export default DeviceAddModal;
